import React, { useState } from 'react';

import { Form } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faImage, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { backgroundGrey5, coolGrey, grape, greyStroke, squidInk, white } from 'lib/css/colors';
import Container from 'components/shared/Container';
import EmptyState from 'components/shared/EmptyState';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import { TEMPORARY_PRODUCT_IMAGE_THUMBNAIL_ASSIGNING_STATS } from 'lib/networking/endpoints';
import useGet from 'lib/hooks/useGet';

function ThumbnailAssigningStats() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const { data: stats, loading } = useGet(TEMPORARY_PRODUCT_IMAGE_THUMBNAIL_ASSIGNING_STATS, {
    start_date: startDate || null,
    end_date: endDate || null,
  });

  const totalSkus = stats?.stats?.reduce((sum, stat) => sum + stat.total_skus, 0) || 0;

  return (
    <Container className="mt-4">
      <div className={css(styles.container)}>
        <div className={css(styles.header)}>
          <FontAwesomeIcon icon={faImage} className={css(styles.headerIcon)} />
          <h3 className={css(styles.title)}>Thumbnail Assigning Stats</h3>
        </div>

        <div className={css(styles.content)}>
          <div className={css(styles.filtersRow)}>
            <div className={css(styles.filterGroup)}>
              <Form.Label className={css(styles.filterLabel)}>
                <FontAwesomeIcon icon={faCalendarAlt} className={css(styles.filterIcon)} />
                Start Date
              </Form.Label>
              <Form.Control
                type="date"
                value={startDate || ''}
                onChange={e => setStartDate(e.target.value)}
                className={css(styles.dateInput)}
              />
            </div>
            <div className={css(styles.filterGroup)}>
              <Form.Label className={css(styles.filterLabel)}>
                <FontAwesomeIcon icon={faCalendarAlt} className={css(styles.filterIcon)} />
                End Date
              </Form.Label>
              <Form.Control
                type="date"
                value={endDate || ''}
                onChange={e => setEndDate(e.target.value)}
                className={css(styles.dateInput)}
              />
            </div>
          </div>

          {loading ? (
            <LoadingSpinner short />
          ) : stats?.stats?.length > 0 ? (
            <div className={css(styles.tableContainer)}>
              <div className={css(styles.tableHeader)}>
                <div className={css(styles.vendorCol)}>Distributor</div>
                <div className={css(styles.skuCol)}>Total SKUs</div>
              </div>
              {stats.stats.map(stat => (
                <div key={stat.vendor_name} className={css(styles.tableRow)}>
                  <div className={css(styles.vendorCol)}>
                    <span className={css(styles.vendorName)}>{stat.vendor_name}</span>
                  </div>
                  <div className={css(styles.skuCol)}>
                    <span className={css(styles.skuValue)}>{stat.total_skus.toLocaleString()}</span>
                  </div>
                </div>
              ))}
              <div className={css(styles.totalRow)}>
                <div className={css(styles.vendorCol)}>
                  <span className={css(styles.totalLabel)}>Total</span>
                </div>
                <div className={css(styles.skuCol)}>
                  <span className={css(styles.totalValue)}>{totalSkus.toLocaleString()}</span>
                </div>
              </div>
            </div>
          ) : (
            <EmptyState
              icon={faImage}
              title="No stats available"
              description="Select a date range to view thumbnail assigning statistics."
            />
          )}
        </div>
      </div>
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: white,
    borderRadius: 8,
    border: `1px solid ${greyStroke}`,
    overflow: 'hidden',
  },
  header: {
    padding: '16px 20px',
    borderBottom: `1px solid ${greyStroke}`,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    backgroundColor: white,
  },
  headerIcon: {
    color: grape,
    fontSize: 18,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: squidInk,
    margin: 0,
  },
  content: {
    padding: 20,
  },
  filtersRow: {
    display: 'flex',
    gap: 16,
    marginBottom: 20,
  },
  filterGroup: {
    flex: 1,
    maxWidth: 180,
  },
  filterLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: squidInk,
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  filterIcon: {
    color: coolGrey,
    fontSize: 12,
  },
  dateInput: {
    borderRadius: 6,
    border: `1px solid ${greyStroke}`,
    padding: '8px 12px',
    fontSize: 14,
  },
  tableContainer: {
    borderRadius: 6,
    border: `1px solid ${greyStroke}`,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: backgroundGrey5,
    borderBottom: `1px solid ${greyStroke}`,
    fontWeight: 600,
    fontSize: 12,
    color: coolGrey,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: `1px solid ${greyStroke}`,
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: `${grape}08`,
    },
  },
  totalRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: backgroundGrey5,
  },
  vendorCol: {
    flex: 1,
  },
  skuCol: {
    width: 100,
    textAlign: 'right',
  },
  vendorName: {
    fontSize: 14,
    fontWeight: 500,
    color: squidInk,
  },
  skuValue: {
    fontSize: 14,
    fontWeight: 500,
    color: squidInk,
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: squidInk,
  },
  totalValue: {
    fontSize: 14,
    fontWeight: 600,
    color: grape,
  },
});

export default ThumbnailAssigningStats;
